import { Box, Button, CircularProgress, Typography } from '@mui/material';
import Drawer, { DrawerHandle } from 'components/drawer';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useParams } from 'react-router-dom';

import _ from 'underscore';
import ApplyAsSelection from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-result-tab/fish-groups/components/apply-as-selection';
import ExcludedFishGroupsList from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-result-tab/fish-groups/components/excluded-fish-groups-list';
import FeedProgramMappingList from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-result-tab/fish-groups/components/feed-program-mapping-list';
import FeedProgramReviewList from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-result-tab/fish-groups/components/feed-program-review-list';
import { useR2ScenarioState } from 'views/bluviu-nutrition/features/scenarios-v2/context/r2-scenario-context';
import { useScenarioFishGroupsState } from 'views/bluviu-nutrition/features/scenarios-v2/context/scenario-fish-groups-context';
import { useScenarioResultState } from 'views/bluviu-nutrition/features/scenarios-v2/context/scenario-result-context';
import { ApplyFeedFormInput } from 'views/bluviu-nutrition/features/scenarios-v2/types/feed-mappings-input.type';
import { ScenarioAppliedAs } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-applied-as.enum';

const STEPS = ['Review', 'Map feeds'] as const;

type ParamTypes = {
  globalSiteId: string;
  scenarioId: string;
};

type StepperTitleProps = {
  step: number;
};

const StepperTitle: React.FC<StepperTitleProps> = ({ step }) => {
  const stepLabel = STEPS[step - 1];
  return (
    <Box display="flex" flexDirection="column">
      <Typography fontWeight={600} fontSize={16}>
        <FormattedMessage id="bluViuNutrition.applyFeed.title" />
      </Typography>
      <Typography color="#415965" fontSize={14}>
        <FormattedMessage id="bluViuNutrition.applyFeed.stepIndicator" values={{ step, total: STEPS.length, stepLabel }} />
      </Typography>
    </Box>
  );
};

const ApplyFeedsDrawerForm: React.FC = () => {
  const { selectedDefaultProgramName } = useScenarioResultState();
  const intl = useIntl();
  const history = useHistory();
  const queryClient = useQueryClient();
  const params = useParams<ParamTypes>();
  const snackbarDispatch = useSnackbarDispatch();

  // #region State and Refs
  const [activeStep, setActiveStep] = useState(1);
  const applyFeedDrawerRef = useRef<DrawerHandle>(null);

  // Mapping state for disabling Confirm and Apply
  const [mappingState, setMappingState] = useState<{ allComplete: boolean; hasError: boolean }>({
    allComplete: false,
    hasError: false,
  });

  const { cageFeedPrograms, feedMappings, applyMutation } = useScenarioFishGroupsState();

  // #endregion

  // #region FORMS
  const defaultValues: ApplyFeedFormInput = {
    appliedAs: ScenarioAppliedAs.PLAN,
    cageFeedPrograms: [],
    feedMappings: [],
  };

  const form = useForm<ApplyFeedFormInput>({
    defaultValues,
    shouldUnregister: false,
    mode: 'onChange',
  });

  const { reset, handleSubmit, watch, setValue } = form;

  const appliedAs = watch('appliedAs');
  // #endregion

  // #region FETCHING DATA
  const { r2ScenarioQuery: { data: scenarioResult } } = useR2ScenarioState();

  const [includedCageData, excludedCageData] = _.partition(scenarioResult?.cageData || [], (cage) => cage.isIncluded);
  // #endregion

  // #region HANDLERS
  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSubmitForm = () => {
    const data: ApplyFeedFormInput = {
      appliedAs,
      cageFeedPrograms,
      feedMappings,
    }
    applyMutation.mutate(data, {
      onSuccess: () => {
        applyFeedDrawerRef.current?.close();
        snackbarDispatch({
          type: SnackbarActionTypes.SUCCESS_SNACKBAR,
          payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
        });
        queryClient.invalidateQueries({
          queryKey: ['getR2Scenario', params.globalSiteId, params.scenarioId]
        });
        history.push(`/bluviu-nutrition/r2/${params.globalSiteId}/${params.scenarioId}/applied`);
      },
      onError: (error) => {
        snackbarDispatch({
          type: SnackbarActionTypes.ERROR_SNACKBAR,
          payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
        });
      }
    });
  };

  const handleCloseDrawer = () => {
    setActiveStep(1);
    reset(defaultValues);
  };

  const handleApplyAsChange = (value: ScenarioAppliedAs) => {
    setValue('appliedAs', value, { shouldDirty: true });
  };

  // #endregion

  const renderActionButtons = () => {
    if (activeStep === 1) {
      return (
        <Button variant="outlined" onClick={handleNext} endIcon={<ArrowRight size={16} />}>
          <FormattedMessage id="general.next" />
        </Button>
      );
    }

    return (
      <Box display="flex" gap={2}>
        <Button variant="outlined" onClick={handleBack} startIcon={<ArrowLeft size={16} />}>
          <FormattedMessage id="general.back" />
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit(handleSubmitForm)}
          disabled={!mappingState.allComplete || mappingState.hasError || applyMutation.isPending}
          startIcon={applyMutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
          <FormattedMessage id="bluViuNutrition.applyFeed.confirmAndApply" />
        </Button>
      </Box>
    );
  };

  return (
    <FormProvider {...form}>
      <Drawer
        actionButtons={renderActionButtons()}
        ref={applyFeedDrawerRef}
        onClose={handleCloseDrawer}
        title={<StepperTitle step={activeStep} />}
        button={
          <Button size="small" variant="contained" disabled={!selectedDefaultProgramName || !!scenarioResult?.appliedAs}>
            <FormattedMessage id="bluViuNutrition.applyFeed.buttonLabel" />
          </Button>
        }>
        <Box display="flex" flexDirection="column" gap={6} width={850}>
          {activeStep === 1 && (
            <>
              <ApplyAsSelection value={appliedAs} onChange={handleApplyAsChange} />
              <FeedProgramReviewList cageData={includedCageData} />
              <ExcludedFishGroupsList cageData={excludedCageData} />
            </>
          )}
          {activeStep === 2 && <FeedProgramMappingList onMappingStateChange={setMappingState} />}
        </Box>
      </Drawer>
    </FormProvider>
  );
};

export default ApplyFeedsDrawerForm;
